import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useT } from '@/shared/lib/i18n';
import { BackArrow } from './icons';

export type TopBarProps = {
  /** When set, render a button that calls this on click. Otherwise renders a Link to `/`. */
  onBack?: () => void;
  /** Optional override label for the back affordance. Defaults to translated `nav.back`. */
  backLabel?: string;
  /** Center title slot — usually the page name. Omit to show nothing in the middle. */
  title?: ReactNode;
  /** Show the brand link on the right. Default true. */
  showBrand?: boolean;
  /** Extra classes on the wrapper (padding/spacing tweaks per page). */
  className?: string;
};

const wrapperBase = 'flex items-center justify-between border-ink/20 border-b border-dashed pb-2.5';

const backCls =
  'flex h-7 items-center gap-1.5 rounded-md bg-ink/[0.06] px-2 font-display font-extrabold text-[10.5px] text-ink uppercase tracking-[0.16em]';

const brandCls = 'eyebrow text-[10px] text-text-dim tracking-[0.16em]';

/**
 * Shared page top-bar: back affordance on the left, optional title in the
 * middle, brand link on the right. Replaces the `border-ink/20 border-b
 * border-dashed` recipe duplicated across lobby, briefing, and round screens.
 */
export const TopBar = ({ onBack, backLabel, title, showBrand = true, className }: TopBarProps) => {
  const t = useT();
  const label = (backLabel ?? t('nav.back')).toUpperCase();
  const ariaLabel = t('nav.home_label');

  const back = onBack ? (
    <button type="button" onClick={onBack} aria-label={ariaLabel} className={backCls}>
      <BackArrow />
      <span>{label}</span>
    </button>
  ) : (
    <Link to="/" aria-label={ariaLabel} className={backCls}>
      <BackArrow />
      <span>{label}</span>
    </Link>
  );

  return (
    <div className={[wrapperBase, className ?? ''].filter(Boolean).join(' ')}>
      {back}
      {title ? <div className="min-w-0 truncate">{title}</div> : <span />}
      {showBrand ? (
        <Link to="/" className={brandCls}>
          {t('nav.brand')}
        </Link>
      ) : (
        <span />
      )}
    </div>
  );
};
